import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input, OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { BaseComponent } from '../../../core/layout/components/abstracts/base.component';
import { IIdAndName } from '../../../core/interfaces/IIdAndName';
import { ISelectOption } from '../../../core/interfaces/ISelectOption';
import { ControlValueAccessor } from '@angular/forms';
import { Timeout } from '../../../core/decorators/timeout.decorator';
import { DOCUMENT } from '@angular/common';
import { Overlay, OverlayRef, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ControlValueAccessorUtils } from '../../../core/utils/ControlValueAccessorUtils';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [ControlValueAccessorUtils.getProvider(SelectComponent)]
})
export class SelectComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {

  @ViewChild('select') selectRef!: ElementRef<HTMLDivElement>;
  @ViewChild('optionWrapper') optionWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('optionsWrapperTemplate') optionsWrapperTemplate!: TemplateRef<HTMLDivElement>;

  @Input() hasResetOption = true;
  @Input() placeholder: string = '';
  @Input() showErrorIcon = true;
  @Input() canUnselect = false;
  @Input() set isRequired(isRequired: boolean) {
    this._isRequired = isRequired;

    if (this._isRequired) {
      this.hasResetOption = false;
    }
  };
  @Input() width = '100%';
  @Input() height = '240px';
  @Input() hasErrors = false;
  @Input() outputType: 'string' | 'number' = 'number';
  @Input() propagateType: 'blur' | 'change' = 'change';
  @Input() openToTop = false;
  @Input() set options(options: IIdAndName[] | ISelectOption[]) {
    this.optionsActions(options);
    this.writeValue(this.currentValue);
  };

  @Output() valueChanged = new EventEmitter<number | string | number[] | string[]>();

  public value: number | string | number[] | string[] = null;
  public isDisabled = false;
  public onChange: Function = () => {};
  public onTouched: Function = () => {};
  public open = false;
  public showOptionsWrapper = false;
  public currentValue: number | string | number[] | string[] = null;

  private _options: ISelectOption[] = [];
  private _isRequired = false;
  private _overlayRef: OverlayRef;
  private _intersectionObserver: IntersectionObserver;
  private readonly _document = inject(DOCUMENT);
  private readonly _layout = this._document.body;
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _scrollStrategyOptions = inject(ScrollStrategyOptions);

  public ngOnInit(): void {
    fromEvent(this._document, 'click', { capture: true }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => this.onDocumentClick(event as MouseEvent));
  }

  public ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  public get isRequired(): boolean {
    return this._isRequired;
  }

  public get options(): ISelectOption[] {
    return this._options;
  }

  public registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.detectChanges();
  }

  public writeValue(value: number | string | number[] | string[]): void {
    this.currentValue = value;

    if (Array.isArray(value)) {
      value = (value as any[]).filter(x => x !== null && x !== undefined).map(x => x.toString().toLowerCase().trim());

      this.optionsActions(this._options.map((option) => ({
        ...option,
        isSelected: (value as string[]).includes(option.id as string)
      })));
    } else if (value !== null && value !== undefined) {
      this.optionsActions(this._options.map((option) => ({
        ...option,
        isSelected: option.id === value.toString().toLowerCase().trim()
      })));
    } else {
      this.optionsActions(this._options.map((option) => ({
        ...option,
        isSelected: false
      })));
    }
  }

  public changeOption(val?: string | number): void {
    if (this.outputType === 'number' && val !== null) {
      val = Number(val);
    }

    this.propagateChanges(val);
  }

  public selectOption(id: string): void {
    this.currentValue = id;
    const isAlreadySelected = this._options.some(option => option.id === id && option.isSelected);

    this.optionsActions(this._options.map((option) => ({
      ...option,
      isSelected: option.id === id?.toString().toLowerCase().trim() ? this.canUnselect ? !option.isSelected : true : false
    })));
    this.open = false;
    this.destroyOverlay();
    this.changeOption(isAlreadySelected ? null : id);
    this.cdr.detectChanges();
  }

  public toggleDropdown(forceClose = false): void {
    if (!this.isDisabled && this.options && this.options.length) {
      setTimeout(() => {
        if (forceClose) {
          this.showOptionsWrapper = false;
        } else {
          if (this._layout) {
            this.open ? this.checkComponentPosition() : this.showOptionsWrapper = false;
          } else {
            this.showOptionsWrapper = true;
          }
        }

        this.cdr.detectChanges();
      });

      if (forceClose) {
        this.open = false;
      } else {
        this.open = !this.open;
      }

      this.cdr.detectChanges();

      this.open ? this.setOverlay() : this.destroyOverlay();
    }
  }

  public propagateChanges(data: number | string | number[] | string[]): void {
    this.onChange(data);
    this.onTouched(data);
    this.cdr.detectChanges();
    this.valueChanged.emit(data);
  }

  public reset(): void {
    this.currentValue = null;
    this.optionsActions(this._options.map((option) => ({...option, isSelected: false})));
    this.propagateChanges(this.currentValue);
  }

  public get displayText(): string {
    return this.options.filter((option) => option.isSelected).map((option) => option.name).join(', ');
  }

  private optionsActions(options: IIdAndName[] | ISelectOption[]): void {
    if (!options) return;

    this._options = options?.length ? options.map((option) => ({
      ...option,
      id: option.id.toString().toLowerCase().trim(),
      isSelected: option.isSelected || false
    })) as ISelectOption[] : [];

    this.cdr.detectChanges();
  }

  @Timeout()
  private checkComponentPosition(): void {
    this.openToTop = (
      Math.round(this.selectRef.nativeElement.getBoundingClientRect().bottom) +
      Math.round(this.optionWrapper?.nativeElement.clientHeight)
    ) > Math.ceil(this._layout.getBoundingClientRect().bottom);

    this.showOptionsWrapper = true;
    this.cdr.detectChanges();
  }

  private onDocumentClick(event: MouseEvent): void {
    if (
      this.open
      && (!this.selectRef.nativeElement.contains(event.target as HTMLElement)
        && !(event.target as HTMLElement).closest('.options-wrapper'))
    ) {
      this.open = false;
      this.destroyOverlay();
      this.cdr.detectChanges();
    }
  }

  private setOverlay(): void {
    if (this.open && this.options?.length) {
      const positionStrategy = this._overlay.position()
        .flexibleConnectedTo(this.selectRef)
        .withPositions([{
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        }, {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom'
        }]);

      const scrollStrategy = this._scrollStrategyOptions.reposition();

      this._overlayRef = this._overlay.create({
        positionStrategy,
        scrollStrategy,
        width: this.selectRef.nativeElement.offsetWidth
      });

      this._overlayRef.attach(new TemplatePortal(this.optionsWrapperTemplate, this._viewContainerRef));

      setTimeout(() => {
        positionStrategy.positionChanges.pipe(
          takeUntilDestroyed(this.destroyRef)
        ).subscribe((position) => {
          const isDifferent = this.openToTop !== (position.connectionPair.originY === 'top');

          if (isDifferent) {
            this.toggleDropdown(true);
          }

          this.cdr.detectChanges();
        });
      });
    }
  }

  private destroyOverlay(): void {
    this._overlayRef?.detach();
    this._overlayRef?.dispose();
  }

  private setupIntersectionObserver(): void {
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && this.open) {
            this.toggleDropdown(true);
          }
        });
      },
      { threshold: 0 }
    );

    this._intersectionObserver.observe(this.selectRef.nativeElement);
  }

  public ngOnDestroy(): void {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
  }
}
